import type { StyleLibrary } from "schemas/generated/project";

import React, { useEffect, useState } from "react";

import Input from "@editor/components/common/designSystem/Input";
import { useCurrentWorkspaceId } from "@editor/contexts/WorkspaceDashboardContext";
import { useLocalStorageState } from "@editor/hooks/useLocalStorage";
import { integrationRouter } from "@editor/trpc/integration";
import { projectRouter } from "@editor/trpc/project";
import { stylesRouter, stylesUtils } from "@editor/trpc/styles";
import { webContainerUtils } from "@editor/trpc/webContainer";

import Button from "@replo/design-system/components/button/Button";
import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import twMerge from "@replo/design-system/utils/twMerge";
import { skipToken } from "@tanstack/react-query";
import Lottie from "lottie-react";
import { Check, PaintbrushVertical } from "lucide-react";
import { z } from "zod";

import OnboardingTemplate from "./OnboardingTemplate";
import styleLoadingAnimation from "./style-loading.json";

function themeToStyleLibrary(theme: string): StyleLibrary {
  const themeFonts = {
    mono: {
      header: "Instrument Serif",
      body: "Inter",
    },
    modern: {
      header: "Helvetica Neue",
      body: "Arial",
    },
    bold: {
      header: "Impact",
      body: "PT Mono",
    },
    natural: {
      header: "Raleway",
      body: "Sansation",
    },
  };

  const fonts = themeFonts[theme as keyof typeof themeFonts] ?? themeFonts.mono;

  const baseTypography = {
    h1: {
      name: fonts.header,
      url: "",
      size: 48,
      weight: 700,
      lineHeight: 0,
      letterSpacing: 0,
    },
    h2: {
      name: fonts.header,
      url: "",
      size: 32,
      weight: 500,
      lineHeight: 0,
      letterSpacing: 0,
    },
    h3: {
      name: fonts.header,
      url: "",
      size: 28,
      weight: 500,
      lineHeight: 0,
      letterSpacing: 0,
    },
    h4: {
      name: fonts.header,
      url: "",
      size: 24,
      weight: 500,
      lineHeight: 0,
      letterSpacing: 0,
    },
    body: {
      name: fonts.body,
      url: "",
      size: 16,
      weight: 400,
      lineHeight: 0,
      letterSpacing: 0,
    },
  };

  const themeColors = {
    mono: {
      neutrals: {
        light: "#FFFFFF",
        subtle: "#F2F2F2",
        muted: "#DDDDDD",
        dark: "#000000",
      },
      brand: {
        primary: {
          color: "#FFFFFF",
          name: "Primary",
        },
        secondary: {
          color: "#000000",
          name: "Secondary",
        },
        tertiary: {
          color: "",
          name: "",
        },
        accent: {
          color: "",
          name: "",
        },
      },
    },
    modern: {
      neutrals: {
        light: "#FFFFFF",
        subtle: "#FAFAFA",
        muted: "#EDEDED",
        dark: "#000000",
      },
      brand: {
        primary: {
          color: "#FF5C1C",
          name: "Primary",
        },
        secondary: {
          color: "#FFF7F4",
          name: "Secondary",
        },
        tertiary: {
          color: "",
          name: "",
        },
        accent: {
          color: "",
          name: "",
        },
      },
    },
    bold: {
      neutrals: {
        light: "#FFFFFF",
        subtle: "#FFFCFA",
        muted: "#F6E9E0",
        dark: "#000000",
      },
      brand: {
        primary: {
          color: "#D0F491",
          name: "Primary",
        },
        secondary: {
          color: "#E9D5FF",
          name: "Secondary",
        },
        tertiary: {
          color: "",
          name: "",
        },
        accent: {
          color: "",
          name: "",
        },
      },
    },
    natural: {
      neutrals: {
        light: "#FFFFFF",
        subtle: "#FFFAF5",
        muted: "#FFF1E5",
        dark: "#3E2003",
      },
      brand: {
        primary: {
          color: "#C8D5AC",
          name: "Primary",
        },
        secondary: {
          color: "#D97B5C",
          name: "Secondary",
        },
        tertiary: {
          color: "",
          name: "",
        },
        accent: {
          color: "",
          name: "",
        },
      },
    },
  };

  const colors =
    themeColors[theme as keyof typeof themeColors] ?? themeColors.mono;

  return {
    colors,
    typography: baseTypography,
    cornerRounding: 8,
  };
}

interface DesignLibraryProps {
  isStyleExtractionInProgress: boolean;
  styleLibrary: any;
  fontFaceCSS: string;
  hasCompletedManualExtraction: boolean;
  extractedFontsCount: number;
  extractedColorsCount: number;
}

const DesignLibrary: React.FC<DesignLibraryProps> = ({
  isStyleExtractionInProgress,
  styleLibrary,
  fontFaceCSS,
  hasCompletedManualExtraction,
  extractedFontsCount,
  extractedColorsCount,
}) => {
  const colorSwatchColors = {
    light: styleLibrary?.colors?.neutrals?.light ?? "#FFFFFF",
    subtle: styleLibrary?.colors?.neutrals?.subtle ?? "#F5F5F5",
    muted: styleLibrary?.colors?.neutrals?.muted ?? "#999999",
    dark: styleLibrary?.colors?.neutrals?.dark ?? "#000000",
    primary: styleLibrary?.colors?.brand?.primary?.color ?? "#000000",
    secondary: styleLibrary?.colors?.brand?.secondary?.color ?? "#FFFFFF",
  };

  const headingFont = styleLibrary?.typography?.h1?.name ?? "Aeonik";
  const bodyFont = styleLibrary?.typography?.body?.name ?? "Inter";

  if (isStyleExtractionInProgress) {
    return (
      <div>
        <div className="px-8 py-4 flex flex-col gap-1 border-b border-border">
          <p className="text-3xl font-semibold">Pulling in Your Styles... 🎨</p>
          <p className="text-muted font-normal">
            This should only take 10-20 seconds.
          </p>
        </div>
        <div className="px-8 py-4 gap-6 flex flex-col border-b border-border">
          <div className="font-semibold">Colors</div>
          <div className="flex items-center self-stretch">
            {Array.from({ length: 6 }).map((_, index) => (
              <div
                key={index}
                className={twMerge("h-16 rounded-lg", index > 0 ? "-ml-4" : "")}
                style={{
                  flex: "1 0 0",
                  alignSelf: "stretch",
                }}
              >
                <Skeleton className="w-full h-full rounded-lg" />
              </div>
            ))}
          </div>
        </div>
        <div className="px-8 py-4 gap-6 flex flex-col">
          <div className="font-semibold">Typography</div>
          <div className="flex flex-col p-3 justify-center items-center self-stretch rounded-lg bg-sheet border-0.5 border-black/10 gap-1">
            <Skeleton className="h-12 w-60" />
            <Skeleton className="h-4 w-100" />
          </div>
          <div className="grid grid-cols-2 gap-3 self-stretch">
            <div className="flex py-3 pr-1 pl-3 flex-col items-start gap-2 border-0.5 border-border rounded-md overflow-hidden min-w-0">
              <div className="text-sm font-medium text-muted">Headings</div>
              <Skeleton className="h-9 w-60" />
            </div>
            <div className="flex py-3 pr-1 pl-3 flex-col items-start gap-2 border-0.5 border-border rounded-md overflow-hidden min-w-0">
              <div className="text-sm font-medium text-muted">Body</div>
              <Skeleton className="h-9 w-60" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {fontFaceCSS && (
        <style
          dangerouslySetInnerHTML={{
            __html: fontFaceCSS,
          }}
        />
      )}

      <div className="px-8 py-6 flex flex-col gap-1 border-b border-border">
        <p className="text-3xl font-semibold">Your Design Library 🎨</p>
        <p className="text-muted font-normal">
          You can always edit this later.
        </p>
      </div>
      <div className="px-8 py-4 gap-6 flex flex-col border-b border-border">
        <div className="font-semibold">Colors</div>
        <div className="flex items-center self-stretch">
          {Object.entries(colorSwatchColors).map(([key, color], index) => (
            <div
              key={key}
              className={twMerge(
                "h-16 rounded-lg border-0.5 border-black/10",
                index > 0 ? "-ml-4" : "",
              )}
              style={{
                backgroundColor: color,
                flex: "1 0 0",
                alignSelf: "stretch",
              }}
            ></div>
          ))}
        </div>
      </div>
      <div
        className={twMerge(
          "px-8 py-4 gap-6 flex flex-col",
          hasCompletedManualExtraction ? "border-b border-border" : "",
        )}
      >
        <div className="font-semibold">Typography</div>
        <div className="flex flex-col p-3 justify-center items-center self-stretch rounded-lg bg-sheet border-0.5 border-black/10 gap-1">
          <div
            className="flex h-8 flex-col justify-center items-center self-stretch text-center"
            style={{
              fontFamily: `'${headingFont}', sans-serif`,
              fontSize: "42px",
              fontWeight: 600,
            }}
          >
            Heading
          </div>
          <div
            style={{
              fontFamily: `'${bodyFont}', sans-serif`,
              fontSize: "16px",
              fontWeight: 400,
            }}
          >
            This is your paragraph.
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 self-stretch">
          <div className="flex py-3 pr-1 pl-3 flex-col items-start gap-2 border-0.5 border-border rounded-md overflow-hidden min-w-0">
            <div className="text-sm font-medium text-muted">Headings</div>
            <div
              className="text-3xl font-semibold truncate min-w-0 w-full"
              style={{
                fontFamily: `'${headingFont}', sans-serif`,
              }}
              title={headingFont}
            >
              {headingFont}
            </div>
          </div>
          <div className="flex py-3 pr-1 pl-3 flex-col items-start gap-2 border-0.5 border-border rounded-md overflow-hidden min-w-0">
            <div className="text-sm font-medium text-muted">Body</div>
            <div
              className="text-3xl font-semibold truncate min-w-0 w-full"
              style={{
                fontFamily: `'${bodyFont}', sans-serif`,
              }}
              title={bodyFont}
            >
              {bodyFont}
            </div>
          </div>
        </div>
      </div>
      {hasCompletedManualExtraction && (
        <div className="px-8 py-4 flex flex-col gap-1">
          <div className="p-3 bg-sheet border border-border rounded-lg">
            <div className="text-sm flex items-start gap-2">
              <PaintbrushVertical
                size={16}
                className="text-muted flex-shrink-0 mt-0.5"
              />
              <span>
                We pulled{" "}
                <span className="font-medium">{extractedFontsCount} fonts</span>{" "}
                and{" "}
                <span className="font-medium">
                  {extractedColorsCount} colors
                </span>{" "}
                from your URL. You can access these in the tool.
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface ThemeSelectionProps {
  selectedTheme: string | null;
  onThemeSelect: (theme: string) => void;
}

const ThemeSelection: React.FC<ThemeSelectionProps> = ({
  selectedTheme,
  onThemeSelect,
}) => {
  return (
    <div className="px-8 pt-6 flex flex-col w-full">
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-1">
          <p className="text-2xl lg:text-3xl font-semibold">
            Choose a Theme 🎨
          </p>
          <p className="text-muted font-normal">
            You can always customize your theme later.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-8">
          <div
            className={twMerge(
              "h-[150px] lg:h-[170px] rounded-xl bg-fill bg-center bg-no-repeat cursor-pointer relative",
              selectedTheme === "mono"
                ? "border-black border-2"
                : "border border-border",
            )}
            style={{ backgroundImage: "url(/images/onboarding/mono.svg)" }}
            onClick={() => onThemeSelect("mono")}
          >
            {selectedTheme === "mono" && (
              <div className="absolute -top-3 -right-3 z-10 w-6 h-6 bg-black rounded-full flex items-center justify-center">
                <Check size={16} className="text-white" />
              </div>
            )}
          </div>
          <div
            className={twMerge(
              "h-[150px] lg:h-[170px] rounded-xl bg-fill bg-center bg-no-repeat cursor-pointer relative",
              selectedTheme === "modern"
                ? "border-black border-2"
                : "border border-border",
            )}
            style={{
              backgroundImage: "url(/images/onboarding/modern.svg)",
            }}
            onClick={() => onThemeSelect("modern")}
          >
            {selectedTheme === "modern" && (
              <div className="absolute -top-3 -right-3 z-10 w-6 h-6 bg-black rounded-full flex items-center justify-center">
                <Check size={16} className="text-white" />
              </div>
            )}
          </div>
          <div
            className={twMerge(
              "h-[150px] lg:h-[170px] rounded-xl bg-fill bg-center bg-no-repeat cursor-pointer relative",
              selectedTheme === "bold"
                ? "border-black border-2"
                : "border border-border",
            )}
            style={{ backgroundImage: "url(/images/onboarding/bold.svg)" }}
            onClick={() => onThemeSelect("bold")}
          >
            {selectedTheme === "bold" && (
              <div className="absolute -top-3 -right-3 z-10 w-6 h-6 bg-black rounded-full flex items-center justify-center">
                <Check size={16} className="text-white" />
              </div>
            )}
          </div>
          <div
            className={twMerge(
              "h-[150px] lg:h-[170px] rounded-xl bg-fill bg-center bg-no-repeat cursor-pointer relative",
              selectedTheme === "natural"
                ? "border-black border-2"
                : "border border-border",
            )}
            style={{
              backgroundImage: "url(/images/onboarding/natural.svg)",
            }}
            onClick={() => onThemeSelect("natural")}
          >
            {selectedTheme === "natural" && (
              <div className="absolute -top-3 -right-3 z-10 w-6 h-6 bg-black rounded-full flex items-center justify-center">
                <Check size={16} className="text-white" />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

interface OnboardingDesignLibraryContentProps {
  projectId?: string;
}

export interface OnboardingDesignLibraryContentData {
  styleLibrary: any;
  selectedTheme: string | null;
  extractedData: any;
  hasCompletedManualExtraction: boolean;
  selectedOption: string | null;
  hasValidColors: (colors: any) => boolean;
  hasValidTypography: (typography: any) => boolean;
  updateStyleLibrary: any;
  projectId: string | undefined;
}

const OnboardingDesignLibraryContent = React.forwardRef<
  { getData: () => OnboardingDesignLibraryContentData },
  OnboardingDesignLibraryContentProps
>(({ projectId: overrideProjectId }, ref) => {
  const [selectedTheme, setSelectedTheme] = useState<string | null>("mono");
  const [url, setUrl] = useState<string>("");
  const [hasTriggeredAutomaticExtraction, setHasTriggeredAutomaticExtraction] =
    useState(false);

  const [hasCompletedManualExtraction, setHasCompletedManualExtraction] =
    useState(false);

  const [selectedOption] = useLocalStorageState(
    "onboarding-setup-selected-option",
    null,
    {
      schema: z.union([z.literal("new"), z.literal("import"), z.null()]),
    },
  );

  const workspaceId = useCurrentWorkspaceId();

  const { data: integrationsData } =
    integrationRouter.getWorkspaceShopifyIntegrationDataV2.useQuery(
      workspaceId ? { workspaceId } : skipToken,
    );

  const { data: userProjects } = projectRouter.listWithStats.useQuery({});
  const projectId = overrideProjectId ?? userProjects?.projects[0]?.id;

  const { mutateAsync: updateStyleLibrary } =
    stylesRouter.createOrUpdateStyleLibrary.useMutation({
      onSuccess: () => {
        void stylesUtils.getStyleLibrary.invalidate();
      },
    });

  const { mutateAsync: updateUrlHeuristics } =
    stylesRouter.createOrUpdateUrlHeuristics.useMutation({
      onSuccess: () => {
        void stylesUtils.getUrlHeuristics.invalidate();
      },
    });

  const { mutateAsync: updateComponentsCss } =
    stylesRouter.createOrUpdateComponentsCss.useMutation({
      onSuccess: () => {
        void stylesUtils.getComponentsCss.invalidate();
      },
    });

  const hasValidColors = (colors: any) => {
    return (
      colors &&
      (Object.values(colors.neutrals ?? {}).some(Boolean) ||
        Object.values(colors.brand ?? {}).some((brand: any) => brand?.color))
    );
  };

  const hasValidTypography = (typography: any) => {
    return (
      typography &&
      Object.values(typography).some((font: any) => font?.name && font?.url)
    );
  };

  const {
    mutateAsync: extractStyles,
    data: extractedData,
    reset: resetExtractedData,
    isPending: isStyleExtractionInProgress,
  } = stylesRouter.extractStyles.useMutation({
    onSuccess: async (data) => {
      const { styleLibrary } = data.styles;
      if (styleLibrary && projectId) {
        const hasColors = hasValidColors(styleLibrary.colors);
        const hasTypography = hasValidTypography(styleLibrary.typography);

        if (hasColors || hasTypography) {
          await updateStyleLibrary({
            projectId,
            styleLibrary,
          });
        }
      }

      if (data.styles.variantsByTag && projectId) {
        await updateUrlHeuristics({
          projectId,
          urlHeuristics: JSON.stringify(data.styles.variantsByTag),
        });
      }

      if (data.styles.componentsCss && projectId) {
        await updateComponentsCss({
          projectId,
          componentsCss: data.styles.componentsCss,
        });
      }

      if (projectId) {
        void webContainerUtils.getFileTree.invalidate({ projectId });
      }
    },
    onError: (error) => {
      console.error("Error extracting styles:", error);
    },
  });

  const shopifyIntegrations = integrationsData?.integrations ?? [];
  const firstIntegration = shopifyIntegrations[0];
  const shopifyStoreUrl = firstIntegration?.config?.shopifyUrl;

  useEffect(() => {
    if (
      shopifyStoreUrl &&
      projectId &&
      selectedOption === "import" &&
      !hasTriggeredAutomaticExtraction
    ) {
      setHasTriggeredAutomaticExtraction(true);
      const processedUrl = shopifyStoreUrl.startsWith("http")
        ? shopifyStoreUrl
        : `https://${shopifyStoreUrl}`;

      void extractStyles({ url: processedUrl, projectId });
    }
  }, [
    shopifyStoreUrl,
    projectId,
    extractStyles,
    selectedOption,
    hasTriggeredAutomaticExtraction,
  ]);

  const styleLibrary = extractedData?.styles.styleLibrary;
  const extractedFonts = extractedData?.styles.uploadedFonts ?? [];

  const createFontFaceCSS = (name: string, url: string) => `
    @font-face {
      font-family: '${name}';
      src: url('${url}') format('woff2'), url('${url}') format('woff');
      font-display: swap;
    }`;

  const fontFaceCSS = [
    ...extractedFonts
      .filter(([name, url]) => name && url)
      .map(([name, url]) => createFontFaceCSS(name as string, url as string)),
    ...Object.values(styleLibrary?.typography ?? {})
      .filter((font: any) => font?.name && font?.url)
      .map((font: any) => createFontFaceCSS(font.name, font.url)),
  ].join("");

  // Expose data via ref for parent components
  React.useImperativeHandle(ref, () => ({
    getData: (): OnboardingDesignLibraryContentData => {
      let styleLibraryToSave = styleLibrary;

      if (!styleLibrary && selectedTheme) {
        styleLibraryToSave = themeToStyleLibrary(selectedTheme);
      }

      return {
        styleLibrary: styleLibraryToSave,
        selectedTheme,
        extractedData,
        hasCompletedManualExtraction,
        selectedOption,
        hasValidColors,
        hasValidTypography,
        updateStyleLibrary,
        projectId,
      };
    },
  }));

  const handleThemeSelect = (theme: string) => {
    setSelectedTheme(theme);
  };

  const handleManualStyleExtraction = async () => {
    if (url.trim() && projectId) {
      resetExtractedData();

      const processedUrl = url.startsWith("http") ? url : `https://${url}`;

      await extractStyles({ url: processedUrl, projectId });
      setHasCompletedManualExtraction(true);
    }
  };

  return (
    <div className="flex-1 relative flex flex-col lg:flex-row lg:h-full">
      <div
        className={twMerge(
          "w-full lg:flex-1 lg:min-w-[600px] relative order-1 lg:order-2 min-h-[50vh] lg:min-h-full flex-shrink-0 overflow-hidden",
          isStyleExtractionInProgress
            ? "bg-sheet"
            : twMerge(
                selectedTheme === "mono" && "bg-black",
                selectedTheme === "modern" && "bg-[#FF5C1C]",
                selectedTheme === "bold" && "bg-[#FCFFF7]",
                selectedTheme === "natural" && "bg-[#D29F8E]",
                !selectedTheme && "bg-black",
              ),
        )}
      >
        {isStyleExtractionInProgress && (
          <div className="flex flex-col items-center justify-center h-full">
            <div
              className="w-[300px] h-[300px] lg:w-[400px] lg:h-[400px] mt-10 lg:-mt-20"
              style={{
                maskImage:
                  "linear-gradient(to bottom, transparent 0%, black 15%, black 85%, transparent 100%)",
                WebkitMaskImage:
                  "linear-gradient(to bottom, transparent 0%, black 15%, black 85%, transparent 100%)",
              }}
            >
              <Lottie
                animationData={styleLoadingAnimation}
                loop={true}
                className="w-full h-full"
              />
            </div>
            <p className="text-muted mt-6 text-center">
              Extracting colors, fonts, and images...
            </p>
          </div>
        )}
        {!isStyleExtractionInProgress &&
          selectedTheme &&
          !extractedData &&
          !hasCompletedManualExtraction && (
            <img
              src={`/images/onboarding/${selectedTheme}-template.png`}
              alt={`${selectedTheme} template`}
              className="absolute top-5 left-0 right-0 bottom-0 w-full h-full object-contain"
            />
          )}
        {!isStyleExtractionInProgress && extractedData && (
          <OnboardingTemplate
            styleLibrary={styleLibrary}
            extractedImages={extractedData.styles.images}
          />
        )}
      </div>
      <div className="w-full h-px lg:w-px lg:h-full bg-border order-2 lg:order-2" />{" "}
      {/* Left panel (themes/colors) - shows second on mobile, first on desktop */}
      <div className="flex flex-col justify-between w-full lg:w-[600px] lg:min-w-[400px] flex-shrink order-3 lg:order-1">
        {selectedOption === "import" ||
        isStyleExtractionInProgress ||
        extractedData ? (
          <DesignLibrary
            isStyleExtractionInProgress={isStyleExtractionInProgress}
            styleLibrary={styleLibrary}
            fontFaceCSS={fontFaceCSS}
            hasCompletedManualExtraction={hasCompletedManualExtraction}
            extractedFontsCount={
              extractedData?.styles.uploadedFonts?.length ?? 0
            }
            extractedColorsCount={extractedData?.styles.colors?.length ?? 0}
          />
        ) : (
          <ThemeSelection
            selectedTheme={selectedTheme}
            onThemeSelect={handleThemeSelect}
          />
        )}

        {!isStyleExtractionInProgress && !hasCompletedManualExtraction && (
          <div className="px-8 pb-8 lg:pb-42 pt-8">
            <div className="h-[110px] border border-border rounded-xl">
              <div className="flex flex-col gap-3 p-4 h-full">
                <div className="flex flex-col gap-1">
                  <p className="text-base font-semibold">
                    Looking for something specific?
                  </p>
                  <p className="text-sm text-muted font-normal">
                    Pull brand styles from any URL.
                  </p>
                </div>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    placeholder="Enter URL"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    size="sm"
                    layoutClassName="flex-1"
                  />
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => void handleManualStyleExtraction()}
                  >
                    Pull styles
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
        {isStyleExtractionInProgress && <div className="px-8 pb-8 lg:pb-42" />}
      </div>
    </div>
  );
});

OnboardingDesignLibraryContent.displayName = "OnboardingDesignLibraryContent";

export default OnboardingDesignLibraryContent;
